/**
 * CriterionSummary — shared collapsed-view rendering for all criterion types.
 *
 * Used by:
 *  - Builder-V2 cards (collapsed mode)
 *  - AI Audience Explorer V2 (criteria groups display)
 *
 * Each consumer can wrap this component with their own className for style overrides.
 */
import React, { useState, useRef, useLayoutEffect } from "react";
import PropTypes from "prop-types";
import { Tooltip } from "react-tooltip";
import {
	engagementOptions,
	deprecatedEngagementOptions,
	customUserDataGAMVideoOptions,
	customUserDataGAMDefaultLookback,
	ttlOptions,
	os as osOptions,
	browser as browserOptions,
	device as deviceOptions,
	queryBuilderOperators,
	queryBuilderDateOperators,
	customUserDataGAMIntegrationMetrics,
} from "../../../utilities/constants";
import "./CriterionSummary.scss";

const DEFAULT_DISPLAY_COUNT = 5;

// ─── Extraction helpers ──────────────────────────────────────────────

/**
 * Extract CPM from a segment title string.
 * Handles patterns like "Segment Name $10 CPM" or "Segment Name - $10 CPM"
 */
function extractCpm(title) {
	const match = title?.match(/[-–—]?\s*([£$€][\d.]+\s*CPM)\s*$/i);
	if (match) {
		return {
			name: title.slice(0, match.index).replace(/\s*[-–—]\s*$/, "").trim(),
			cpm: match[1],
		};
	}
	return { name: title, cpm: null };
}

/**
 * Format a snake_case taxonomy property into a readable label.
 * e.g. "business_and_finance" → "Business & Finance", "film_and_tv" → "Film & TV"
 */
function formatTaxonomyProperty(property) {
	const ACRONYMS = new Set(["tv", "api", "ui", "id"]);
	return property
		.replace(/_and_/g, " & ")
		.replace(/_/g, " ")
		.replace(/\b\w+/g, (word) =>
			ACRONYMS.has(word.toLowerCase())
				? word.toUpperCase()
				: word.charAt(0).toUpperCase() + word.slice(1),
		);
}

/**
 * Site-content items (shared by contentConsumption + realtimeSiteContent).
 * Extracts chips from criteria based on criteria.type.
 */
function getSiteContentItems(criteria, criteriaConfiguration) {
	const items = [];
	if (!criteria) return items;

	// Promote taxonomy wrapper: { type: "taxonomy", taxonomy: { type: "default", values: [...] } }
	// becomes { type: "default", taxonomy: { values: [...] } } so the "default" case handles it.
	const effectiveCriteria =
		criteria.type === "taxonomy" && criteria.taxonomy?.type
			? { ...criteria, type: criteria.taxonomy.type }
			: criteria;

	switch (effectiveCriteria.type) {
		case "categories":
			(effectiveCriteria.categories || []).forEach((cat) => {
				const text = typeof cat === "string" ? (cat.includes("|") ? cat.split("|")[1] : cat) : cat.text;
				const isParent = cat?.isParent || false;
				items.push({ label: isParent ? `All ${text}` : text });
			});
			break;
		case "keywords":
			(criteria.keywords || []).forEach((kw) => {
				items.push({ label: typeof kw === "string" ? kw : kw.text || kw.name || String(kw) });
			});
			break;
		case "urls":
			(criteria.urls || []).forEach((url) => {
				(url.values || []).forEach((v) => {
					items.push({ label: typeof v === "string" ? v : v.text || v.name || String(v) });
				});
			});
			break;
		case "garm":
			(criteria.garm || []).forEach((g) => {
				const text =
					typeof g === "string" ? (g.includes("|") ? g.split("|")[1] : g) : g.title || g.text;
				items.push({ label: text });
			});
			break;
		case "sentiments":
			(criteria.sentiments || []).forEach((s) => {
				const text =
					typeof s === "string" ? (s.includes("|") ? s.split("|")[1] : s) : s.title || s.text;
				items.push({ label: text });
			});
			break;
		case "default":
			(criteria.taxonomy?.values || []).forEach((t) => {
				const label =
					t.property && t.value
						? `${formatTaxonomyProperty(t.property)}: ${t.value}`
						: t.value || t.label || String(t);
				items.push({ label });
			});
			break;
		case "custom": {
			const filterBy = criteria.custom?.filterBy;
			if (filterBy?.rules?.length > 0) {
				const customEventType = criteria.custom?.type;
				const customConfig = criteriaConfiguration?.find?.((el) => el.event === customEventType);
				const filters = customConfig?.filters || [];
				const allOps = [...queryBuilderOperators, ...(queryBuilderDateOperators || [])];
				const combinator = filterBy.combinator || "or";

				const ruleGroups = filterBy.rules
					.map((rule) => {
						const filterConfig = filters.find((f) => f.field === rule.field);
						return expandRuleToItems(rule, filterConfig, allOps);
					})
					.filter((group) => group.length > 0);
				ruleGroups.forEach((group, index) => {
					if (index > 0) {
						items.push({ label: combinator.toUpperCase(), isCombinator: true });
					}
					items.push(...group);
				});
			}
			break;
		}
		case "all":
			// No content chips — modifier-only criterion
			break;
		default: {
			// Try to pull items from the type key directly
			const directItems = criteria[criteria.type];
			if (Array.isArray(directItems)) {
				directItems.forEach((item) => {
					items.push({
						label:
							typeof item === "string"
								? item
								: item.text || item.name || item.title || JSON.stringify(item),
					});
				});
			}
		}
	}

	return items;
}

/** Format a QueryBuilder rule value for display (scalar values only). */
function formatRuleValue(value, filterConfig) {
	if (Array.isArray(value)) {
		const nonEmpty = value.filter((v) => v !== undefined && v !== null && String(v).trim() !== "");
		if (nonEmpty.length === 0) return "(empty)";
		const mapVal = (v) => {
			if (filterConfig?.valueMap?.length > 0) {
				const mapped = filterConfig.valueMap.find((m) => m.value === v);
				if (mapped) return mapped.label;
			}
			return String(v);
		};
		if (nonEmpty.length <= 3) return nonEmpty.map(mapVal).join(", ");
		return `${nonEmpty.slice(0, 2).map(mapVal).join(", ")} +${nonEmpty.length - 2} more`;
	}
	let display = String(value);
	if (filterConfig?.valueMap?.length > 0) {
		const mapped = filterConfig.valueMap.find((m) => m.value === value);
		if (mapped) display = mapped.label;
	}
	return display;
}

/**
 * Expand a single QueryBuilder rule into one chip per array element.
 * Scalar values produce a single-element array.
 */
function expandRuleToItems(rule, filterConfig, allOps) {
	// Skip rules with no value
	if (Array.isArray(rule.value)) {
		const nonEmpty = rule.value.filter(
			(v) => v !== undefined && v !== null && String(v).trim() !== "",
		);
		if (nonEmpty.length === 0) return [];
	} else if (rule.value === undefined || rule.value === null || rule.value === "") {
		return [];
	}

	const fieldLabel = filterConfig?.label || rule.field;
	const opConfig = allOps.find((op) => op.name === rule.operator);
	const operatorLabel = opConfig?.label || rule.operator;

	if (Array.isArray(rule.value)) {
		const nonEmpty = rule.value.filter(
			(v) => v !== undefined && v !== null && String(v).trim() !== "",
		);
		return nonEmpty.map((v) => {
			let display = String(v);
			if (filterConfig?.valueMap?.length > 0) {
				const mapped = filterConfig.valueMap.find((m) => m.value === v);
				if (mapped) display = mapped.label;
			}
			return { label: `${fieldLabel} ${operatorLabel} ${display}` };
		});
	}

	const displayValue = formatRuleValue(rule.value, filterConfig);
	return [{ label: `${fieldLabel} ${operatorLabel} ${displayValue}` }];
}

/**
 * Build modifier JSX for contentConsumption (behavioral) criteria.
 * Returns { node, customFilterItems? } or null.
 */
function buildModifierNode(modifier, modifierConfiguration) {
	if (!modifier || !modifier.type) return null;

	const lookbackLabel =
		ttlOptions.find((opt) => opt.value === modifier.lookback)?.label || `${modifier.lookback} days`;
	const highlight = (value) => <span className="cs-modifier-value">{value}</span>;

	// Custom modifier (e.g. "custom|my_event")
	if (modifier.type.startsWith("custom|")) {
		const eventName = modifier.type.split("|")[1];
		const customConfig = modifierConfiguration?.find?.((el) => el.event === eventName);
		if (!customConfig) return null;

		// Build custom filter items
		let customFilterItems = [];
		const filterBy = modifier.filterBy;
		if (filterBy?.rules?.length > 0) {
			const filters = customConfig.filters || [];
			const allOps = [...queryBuilderOperators, ...(queryBuilderDateOperators || [])];
			const combinator = filterBy.combinator || "or";

			const ruleGroups = filterBy.rules
				.map((rule) => {
					const filterConfig = filters.find((f) => f.field === rule.field);
					return expandRuleToItems(rule, filterConfig, allOps);
				})
				.filter((group) => group.length > 0);
			ruleGroups.forEach((group, index) => {
				if (index > 0) {
					customFilterItems.push({ label: combinator.toUpperCase(), isCombinator: true });
				}
				customFilterItems.push(...group);
			});
		}

		return {
			node: (
				<>
					{customConfig.actionLabel} {highlight(modifier.count || 0)}{" "}
					{customConfig.actionQuantityLabel} in {highlight(lookbackLabel)}
				</>
			),
			customFilterItems,
		};
	}

	// Standard modifier (deprecated ones still summarise so existing cohorts stay readable — ASB-3720)
	const modOption = [...engagementOptions, ...deprecatedEngagementOptions].find((opt) => opt.value === modifier.type);
	if (!modOption) return null;

	if (modifier.type === "timeSpent") {
		return {
			node: (
				<>
					{modOption.line} {highlight(modifier.count || 0)}{" "}
					{highlight(modifier.timeUnit || "minutes")} {modOption.line2} {highlight(lookbackLabel)}
				</>
			),
		};
	}

	return {
		node: (
			<>
				{modOption.line} {highlight(modifier.count || 0)} {modOption.line2}{" "}
				{highlight(lookbackLabel)}
			</>
		),
	};
}

/** Criteria type label map. */
const CRITERIA_TYPE_LABELS = {
	categories: "IAB Categories",
	keywords: "Keywords",
	urls: "URLs",
	garm: "Brand Safety",
	sentiments: "Sentiments",
	default: "Custom Taxonomy",
	custom: "Custom Filter",
	all: "All Content",
};

function getCriteriaTypeLabel(type, criteriaConfiguration, customEventType) {
	if (type === "custom" && criteriaConfiguration && customEventType) {
		const config = criteriaConfiguration.find((el) => el.event === customEventType);
		if (config?.label) return config.label;
	}
	return CRITERIA_TYPE_LABELS[type] || (type ? type.charAt(0).toUpperCase() + type.slice(1) : null);
}

// ─── Per-type item extractors ────────────────────────────────────────

function getDemographicItems(data) {
	const items = [];
	const fields = [
		{ key: "age", label: "Age" },
		{ key: "gender", label: "Gender" },
		{ key: "householdIncome", label: "Income" },
		{ key: "jobTitle", label: "Occupation" },
		{ key: "childrenInHousehold", label: "Children" },
		{ key: "lengthOfResidence", label: "Residence" },
		{ key: "homeownerOrRenter", label: "Home" },
		{ key: "maritalStatus", label: "Marital" },
		{ key: "educationLevel", label: "Education" },
	];
	fields.forEach(({ key, label }) => {
		(data?.[key] || []).forEach((item) => {
			items.push({ category: label, label: item.title || item.name || String(item) });
		});
	});
	return items;
}

function getLocationItems(data) {
	const items = [];
	(data?.countries || []).forEach((c) => {
		items.push({ label: c.country_name || c.name || c.id || String(c) });
	});
	(data?.regions || []).forEach((r) => {
		const name = r.region_name || r.name || r.id || String(r);
		items.push({ label: r.country_code ? `${name}, ${r.country_code}` : name });
	});
	(data?.metros || []).forEach((m) => {
		const name = m.metro_name || m.name || m.id || String(m);
		items.push({ label: `${name} DMA` });
	});
	(data?.cities || []).forEach((c) => {
		const name = c.city_name || c.name || c.id || String(c);
		const parts = [name];
		if (c.region_name) parts.push(c.region_name);
		if (c.country_code) parts.push(c.country_code);
		items.push({ label: parts.join(", ") });
	});
	(data?.postalCodes || []).forEach((p) => {
		if (typeof p === "string") {
			if (p.trim()) items.push({ label: p });
		} else {
			items.push({ label: p.code || String(p) });
		}
	});
	return items;
}

function getTechnographicItems(data) {
	const items = [];
	if (data?.os) {
		Object.entries(data.os).forEach(([key, value]) => {
			if (value) {
				const opt = osOptions.find((o) => o.value === key);
				if (opt) items.push({ category: "OS", label: opt.label });
			}
		});
	}
	if (data?.browser) {
		Object.entries(data.browser).forEach(([key, value]) => {
			if (value) {
				const opt = browserOptions.find((o) => o.value === key);
				if (opt) items.push({ category: "Browser", label: opt.label });
			}
		});
	}
	if (data?.deviceType) {
		Object.entries(data.deviceType).forEach(([key, value]) => {
			if (value) {
				const opt = deviceOptions.find((o) => o.value === key);
				if (opt) items.push({ category: "Device", label: opt.label });
			}
		});
	}
	return items;
}

function getDomainItems(data) {
	return (data?.domains || []).map((d) => ({
		label: typeof d === "string" ? d : d.domain || String(d),
	}));
}

function getSyndicatedItems(data, syndicatedOptions) {
	const segs = data?.segments || [];
	if (segs.length === 0) return [];

	// Build provider map if options available
	let providerMap = {};
	if (syndicatedOptions?.length) {
		for (const provider of syndicatedOptions) {
			const providerName = provider.title || provider.label || provider.name;
			for (const cat of provider.children || []) {
				for (const seg of cat.children || []) {
					const id = seg.value ? String(seg.value).split("|")[0] : seg.key || seg.id;
					providerMap[id] = providerName;
				}
			}
		}
	}

	return segs.map((seg) => {
		const { name, cpm } = extractCpm(seg.title || seg.name || String(seg.id));
		const provider = providerMap[seg.id] || seg.provider || null;
		return { label: name, bold: provider, cpm };
	});
}

function getSubCohortItems(data) {
	return (data?.cohorts || []).map((c) => ({
		label: c.name || c.unique_id || String(c),
	}));
}

function getCustomUserDataItems(data, customUserDataConfiguration) {
	const items = [];

	if (data?.type === "gam" && data.gam) {
		// GAM: metric badge + entity chips
		if (data.gam.metric) {
			const metricOption = customUserDataGAMIntegrationMetrics.find(
				(m) => m.value === data.gam.metric,
			);
			items.push({
				label: metricOption?.label || data.gam.metric.replace(/_/g, " "),
				isBadge: true,
			});
		}

		// ASB-3720: threshold + lookback ("2 or more in the past 1 month") and the video split.
		// Rules saved before ASB-3720 carry neither and read as before.
		if (data.gam.count != null || data.gam.lookback != null) {
			const lookback = data.gam.lookback ?? customUserDataGAMDefaultLookback;
			const lookbackLabel = ttlOptions.find((opt) => opt.value === lookback)?.label || `${lookback} days`;
			items.push({ label: `${data.gam.count ?? 1} or more in the past ${lookbackLabel}`, isBadge: true });
		}
		if (typeof data.gam.video === "boolean") {
			const videoOption = customUserDataGAMVideoOptions.find((opt) => opt.video === data.gam.video);
			items.push({ label: videoOption?.label ?? (data.gam.video ? "Video ads only" : "Display ads only"), isBadge: true });
		}

		const entityTypes = [
			{ key: "gamCompanies", label: "Company" },
			{ key: "gamOrders", label: "Order" },
			{ key: "gamLineItems", label: "Line Item" },
			{ key: "gamCreatives", label: "Creative" },
		];
		entityTypes.forEach(({ key, label }) => {
			(data.gam[key] || []).forEach((entity) => {
				items.push({
					label:
						entity.name && entity.name !== "Unknown"
							? `${label}: ${entity.name}`
							: `${label}: ID ${entity.id}`,
				});
			});
		});
	}

	if (data?.type === "custom" && data.custom?.filterBy?.rules) {
		const selectedIntegration = customUserDataConfiguration?.find?.(
			(el) => el.event === data.custom.type,
		);
		const filters = selectedIntegration?.filters || [];
		const allOps = [...queryBuilderOperators];
		const combinator = data.custom.filterBy.combinator || "or";

		const ruleGroups = data.custom.filterBy.rules
			.map((rule) => {
				const filterConfig = filters.find((f) => f.name === rule.field || f.field === rule.field);
				return expandRuleToItems(rule, filterConfig, allOps);
			})
			.filter((group) => group.length > 0);
		ruleGroups.forEach((group, index) => {
			if (index > 0) {
				items.push({ label: combinator.toUpperCase(), isCombinator: true });
			}
			items.push(...group);
		});
	}

	return items;
}

function getIntegrationTitle(data, customUserDataConfiguration) {
	if (data?.type === "gam") return "Google Ad Manager";
	if (data?.type === "custom") {
		const integration = customUserDataConfiguration?.find?.(
			(el) => el.event === data.custom?.type,
		);
		if (!integration) return "Custom Integration";
		return integration.label;
	}
	return null;
}

// ─── Shared chip rendering primitives ────────────────────────────────

/**
 * Single chip with CSS-driven ellipsis. The browser truncates the text
 * span based on actual available width (parent container, font metrics,
 * sibling chips on the same row); we measure scrollWidth vs clientWidth
 * after layout to decide whether to attach the tooltip. Re-measures via
 * ResizeObserver so resizing the container updates the tooltip state.
 */
function Chip({ item }) {
	const textRef = useRef(null);
	const [isOverflowing, setIsOverflowing] = useState(false);

	useLayoutEffect(() => {
		const el = textRef.current;
		if (!el) return undefined;
		const measure = () => setIsOverflowing(el.scrollWidth > el.clientWidth);
		measure();
		const ro = new ResizeObserver(measure);
		ro.observe(el);
		return () => ro.disconnect();
	}, [item.label, item.bold]);

	const tooltipHtml = item.bold
		? `<strong>${item.bold}</strong> ${item.label}`
		: item.label;

	return (
		<span
			className="cs-chip"
			{...(isOverflowing
				? {
						"data-tooltip-id": "chip-truncate-tooltip",
						"data-tooltip-html": tooltipHtml,
					}
				: {})}
		>
			<span className="cs-chip-text" ref={textRef}>
				{item.bold && <strong>{item.bold}</strong>}
				{item.bold ? ` ${item.label}` : item.label}
			</span>
			{item.cpm && (
				<>
					{" - "}
					<span className="cs-chip-cpm">{item.cpm}</span>
				</>
			)}
		</span>
	);
}

Chip.propTypes = {
	item: PropTypes.object.isRequired,
};

function ChipList({ items, maxChips }) {
	const [showAll, setShowAll] = useState(false);
	if (!items || items.length === 0) return null;

	const visible = showAll ? items : items.slice(0, maxChips);
	const remaining = items.length - maxChips;

	return (
		<div className="cs-chips">
			{visible.map((item, i) => {
				if (item.isCombinator) {
					return (
						<span key={`comb-${i}`} className="cs-combinator">
							{item.label}
						</span>
					);
				}
				return <Chip key={`${item.label}-${i}`} item={item} />;
			})}
			{remaining > 0 && !showAll && (
				<span className="cs-more" onClick={() => setShowAll(true)}>
					+{remaining} more
				</span>
			)}
			{showAll && items.length > maxChips && (
				<span className="cs-more" onClick={() => setShowAll(false)}>
					Show less
				</span>
			)}
			<Tooltip id="chip-truncate-tooltip" positionStrategy="fixed" />
		</div>
	);
}

ChipList.propTypes = {
	items: PropTypes.array,
	maxChips: PropTypes.number,
};

// ─── Main component ──────────────────────────────────────────────────

function CriterionSummary({
	type,
	data,
	className,
	maxChips = DEFAULT_DISPLAY_COUNT,
	syndicatedOptions,
	criteriaConfiguration,
	modifierConfiguration,
	customUserDataConfiguration,
	emptyMessage,
}) {
	const rootClass = ["criterion-summary", className].filter(Boolean).join(" ");

	// ── Content Consumption ──
	if (type === "contentConsumption") {
		const action = data?.action || "include";
		const criteria = data?.criteria;
		const criteriaType =
			criteria?.type === "taxonomy" && criteria?.taxonomy?.type
				? criteria.taxonomy.type
				: criteria?.type;
		const items = getSiteContentItems(criteria, criteriaConfiguration);
		const modResult = buildModifierNode(data?.modifier, modifierConfiguration);
		const modNode = modResult?.node;
		const isCustomMod = data?.modifier?.type?.startsWith("custom|");
		const customFilterItems = modResult?.customFilterItems || [];
		const hasCriteria = criteriaType && criteriaType !== "all" && criteriaType !== null;
		const typeBadgeLabel = hasCriteria
			? getCriteriaTypeLabel(criteriaType, criteriaConfiguration, criteria?.custom?.type)
			: null;

		return (
			<div className={rootClass}>
				<div className="cs-summary">
					{action === "exclude" && (
						<span className="cs-action-badge">Exclude</span>
					)}
					{modNode ? (
						<span className="cs-modifier">{modNode}</span>
					) : (
						<span className="cs-empty">No modifier selected</span>
					)}

					{/* Standard modifier: criteria chips */}
					{!isCustomMod && hasCriteria && items.length > 0 && (
						<>
							<span className="cs-modifier">where</span>
							{typeBadgeLabel && (
								<span className="cs-type-badge">{typeBadgeLabel}</span>
							)}
							<ChipList items={items} maxChips={maxChips} />
						</>
					)}

					{/* Custom modifier: filter rule chips */}
					{isCustomMod && customFilterItems.length > 0 && (
						<>
							<span className="cs-modifier">where</span>
							<ChipList items={customFilterItems} maxChips={maxChips} />
						</>
					)}
				</div>
			</div>
		);
	}

	// ── Realtime Site Content ──
	if (type === "realtimeSiteContent") {
		const action = data?.action || "include";
		const criteria = data?.criteria;
		const criteriaType =
			criteria?.type === "taxonomy" && criteria?.taxonomy?.type
				? criteria.taxonomy.type
				: criteria?.type;
		const items = getSiteContentItems(criteria, criteriaConfiguration);
		const typeBadgeLabel = criteriaType
			? getCriteriaTypeLabel(criteriaType, criteriaConfiguration, criteria?.custom?.type)
			: null;

		// Contextual match-all ("Everyone" auto-generated cohort, ASB-3244): there are no content
		// chips to show, but it is NOT empty — render a clear "All site content" badge instead of
		// the misleading "No content criteria selected" empty state.
		if (criteria?.type === "all") {
			return (
				<div className={rootClass}>
					<div className="cs-summary">
						{action === "exclude" && (
							<span className="cs-action-badge">Exclude</span>
						)}
						<span className="cs-type-badge">All site content</span>
					</div>
				</div>
			);
		}

		if (items.length === 0) {
			return (
				<div className={rootClass}>
					<div className="cs-summary">
						<span className="cs-empty">
							{emptyMessage || "No content criteria selected"}
						</span>
					</div>
				</div>
			);
		}

		return (
			<div className={rootClass}>
				<div className="cs-summary">
					{action === "exclude" && (
						<span className="cs-action-badge">Exclude</span>
					)}
					{typeBadgeLabel && <span className="cs-type-badge">{typeBadgeLabel}</span>}
					<ChipList items={items} maxChips={maxChips} />
				</div>
			</div>
		);
	}

	// ── Syndicated ──
	if (type === "syndicated") {
		const items = getSyndicatedItems(data, syndicatedOptions);
		if (items.length === 0) {
			return (
				<div className={rootClass}>
					<div className="cs-summary">
						<span className="cs-empty">{emptyMessage || "No segments selected"}</span>
					</div>
				</div>
			);
		}
		return (
			<div className={rootClass}>
				<div className="cs-summary">
					<ChipList items={items} maxChips={maxChips} />
				</div>
			</div>
		);
	}

	// ── Demographics ──
	if (type === "demographics") {
		const items = getDemographicItems(data);
		if (items.length === 0) {
			return (
				<div className={rootClass}>
					<div className="cs-summary">
						<span className="cs-empty">
							{emptyMessage || "No demographics selected"}
						</span>
					</div>
				</div>
			);
		}
		return (
			<div className={rootClass}>
				<div className="cs-summary">
					<ChipList
						items={items.map((item) => ({
							label: `${item.category}: ${item.label}`,
						}))}
						maxChips={maxChips}
					/>
				</div>
			</div>
		);
	}

	// ── Location ──
	if (type === "location") {
		const action = data?.action || "include";
		const items = getLocationItems(data);
		if (items.length === 0) {
			return (
				<div className={rootClass}>
					<div className="cs-summary">
						<span className="cs-empty">{emptyMessage || "No locations selected"}</span>
					</div>
				</div>
			);
		}
		return (
			<div className={rootClass}>
				<div className="cs-summary">
					{action === "exclude" && (
						<span className="cs-action-badge">Exclude</span>
					)}
					<ChipList items={items} maxChips={maxChips} />
				</div>
			</div>
		);
	}

	// ── Technographics ──
	if (type === "technographics") {
		const items = getTechnographicItems(data);
		if (items.length === 0) {
			return (
				<div className={rootClass}>
					<div className="cs-summary">
						<span className="cs-empty">
							{emptyMessage || "No technographics selected"}
						</span>
					</div>
				</div>
			);
		}
		return (
			<div className={rootClass}>
				<div className="cs-summary">
					<ChipList
						items={items.map((item) => ({ label: `${item.category}: ${item.label}` }))}
						maxChips={maxChips}
					/>
				</div>
			</div>
		);
	}

	// ── Domains ──
	if (type === "domains") {
		const items = getDomainItems(data);
		if (items.length === 0) {
			return (
				<div className={rootClass}>
					<div className="cs-summary">
						<span className="cs-empty">{emptyMessage || "No domains selected"}</span>
					</div>
				</div>
			);
		}
		return (
			<div className={rootClass}>
				<div className="cs-summary">
					<ChipList items={items} maxChips={maxChips} />
				</div>
			</div>
		);
	}

	// ── Sub-Cohorts ──
	if (type === "subCohorts") {
		const action = data?.action || "include";
		const items = getSubCohortItems(data);
		if (items.length === 0) {
			return (
				<div className={rootClass}>
					<div className="cs-summary">
						<span className="cs-empty">{emptyMessage || "No cohorts selected"}</span>
					</div>
				</div>
			);
		}
		return (
			<div className={rootClass}>
				<div className="cs-summary">
					{action === "exclude" && (
						<span className="cs-action-badge">Exclude</span>
					)}
					<ChipList items={items} maxChips={maxChips} />
				</div>
			</div>
		);
	}

	// ── Custom User Data ──
	if (type === "customUserData") {
		const items = getCustomUserDataItems(data, customUserDataConfiguration);
		const integrationTitle = getIntegrationTitle(data, customUserDataConfiguration);
		if (items.length === 0) {
			return (
				<div className={rootClass}>
					<div className="cs-summary">
						<span className="cs-empty">
							{emptyMessage || "No custom user data configured"}
						</span>
					</div>
				</div>
			);
		}
		return (
			<div className={rootClass}>
				<div className="cs-summary">
					{integrationTitle && (
						<span className="cs-integration-badge">{integrationTitle}</span>
					)}
					<ChipList items={items} maxChips={maxChips} />
				</div>
			</div>
		);
	}

	// ── Fallback ──
	return (
		<div className={rootClass}>
			<div className="cs-summary">
				<pre className="cs-json">{JSON.stringify(data, null, 2)}</pre>
			</div>
		</div>
	);
}

CriterionSummary.propTypes = {
	/** Criterion type (e.g. "contentConsumption", "demographics", "syndicated"). */
	type: PropTypes.string.isRequired,
	/** Criterion data in internal (post-transform) format. */
	data: PropTypes.object,
	/** Additional CSS class for style overrides. */
	className: PropTypes.string,
	/** Max visible chips before "show more" (default 5). */
	maxChips: PropTypes.number,
	/** Syndicated tree options — enables provider name resolution. */
	syndicatedOptions: PropTypes.array,
	/** Criteria configuration for custom realtime criteria labels. */
	criteriaConfiguration: PropTypes.array,
	/** Modifier configuration for custom content-consumption modifiers. */
	modifierConfiguration: PropTypes.array,
	/** Custom user data configuration for integration labels and filter configs. */
	customUserDataConfiguration: PropTypes.array,
	/** Override empty-state message. */
	emptyMessage: PropTypes.string,
};

export default CriterionSummary;
